import React, { useRef, useContext, useState, useEffect } from "react";
import { FaPlus } from "react-icons/fa";
import axios from "axios";
import { Link } from "react-router-dom";
import DeleteButton from "../../components/UI/AlertDialog";
import Notification from "../../components/UI/Notification";
import { AuthContext } from "../../contexts/AuthContext";
import Drawer from "../../components/UI/Drawer";
import Modal from "../../components/UI/Modal";
import ReactPaginate from "react-paginate";
import {
  X,
  Calendar,
  Clock,
  LogIn,
  LogOut,
  AlertTriangle,
  MoreVertical,
} from "lucide-react";
import { getToken } from "../../utils/tokenUtils";
import { getRoleBasedRoute } from "../../utils/routeUtils";
import DropdownMenu from "../../components/UI/DropdownMenu";
import StatusBadge from "../../components/UI/StatusBadge";

// const API_URL = "https://hrmspayroll.prepstripe.com/api";

const SwipeRequest = () => {
  const [requests, setRequests] = useState([]);
  const { user } = useContext(AuthContext);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingRequest, setEditingRequest] = useState(null);
  const [filter, setFilter] = useState("All");
  const [notification, setNotification] = useState(null);
  const [formData, setFormData] = useState({
    swipe_type: "Swipe",
    category: "SwipeReq",
    request_date: "",
    in_out_mode: "In",
    in_time: "10:00 AM",
    out_time: "",
    reason: "",
  });
  const [formErrors, setFormErrors] = useState({});
  const token = getToken();
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    perPage: 15,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [isFiltering, setIsFiltering] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // filter
  const [searchQuery, setSearchQuery] = useState("");
  const [showFilterMenu, setShowFilterMenu] = useState(false);
  const [activeFilters, setActiveFilters] = useState({});
  const [filteredData, setFilteredData] = useState(requests);
  const [tempFilters, setTempFilters] = useState({});
  const { roles } = useContext(AuthContext);
  const [openMenu, setOpenMenu] = useState(null);
  const buttonRef = useRef(null);

  const getRoleBasedRouteLocal = (route) => {
    return getRoleBasedRoute(route, roles);
  };

  const toggleFilterMenu = () => {
    setShowFilterMenu(!showFilterMenu);
  };

  const handleFilterChange = (field, value) => {
    setActiveFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const clearFilters = () => {
    setActiveFilters({});
    setSearchQuery("");
  };

  const clearFilterByKey = (key) => {
    const updatedFilters = { ...activeFilters };
    delete updatedFilters[key];
    setActiveFilters(updatedFilters);
  };

  // Define the filterable columns based on your table structure
  const filterableColumns = [
    { field: "swipe_type", label: "Swipe Type" },
    { field: "request_date", label: "Request Date" },
    { field: "in_out_mode", label: "In/Out Mode" },
    { field: "status", label: "Status" },
  ];

  useEffect(() => {
    if (showFilterMenu) {
      setTempFilters(activeFilters);
    }
  }, [showFilterMenu]);

  useEffect(() => {
    setFilteredData(requests);
  }, [requests]);

  useEffect(() => {
    // Apply both search query and filters
    const normalizedRequests = requests.map((req) => ({
      ...req,
      status: req.request?.status || "",
    }));

    let result = normalizedRequests;
    // let result = requests;

    // Apply search query filtering
    if (searchQuery) {
      const lowercasedQuery = searchQuery.toLowerCase();
      result = result.filter((item) => {
        // Search across all relevant fields
        return (
          item.swipe_type?.toLowerCase().includes(lowercasedQuery) ||
          item.request_date?.toLowerCase().includes(lowercasedQuery) ||
          item.in_out_mode
            ?.toString()
            .toLowerCase()
            .includes(lowercasedQuery) ||
          item.in_time?.toLowerCase().includes(lowercasedQuery) ||
          item.out_time?.toLowerCase().includes(lowercasedQuery) ||
          item.status?.toLowerCase().includes(lowercasedQuery)
        );
      });
    }

    // Apply column-specific filters
    Object.entries(activeFilters).forEach(([field, filterValue]) => {
      if (filterValue) {
        result = result.filter((item) => {
          const value = item[field];
          if (field === "created_at" && value) {
            // Date filtering
            const itemDate = new Date(value);
            const day = String(itemDate.getDate()).padStart(2, "0");
            const month = String(itemDate.getMonth() + 1).padStart(2, "0");
            const year = itemDate.getFullYear();
            const formattedDate = `${day}-${month}-${year}`;

            return formattedDate.includes(filterValue);
          } else {
            // Standard string filtering
            return value
              ?.toString()
              .toLowerCase()
              .includes(filterValue.toLowerCase());
          }
        });
      }
    });

    const filteringNow =
      searchQuery.length > 0 || Object.values(activeFilters).some((v) => !!v);
    setIsFiltering(filteringNow);
    setFilteredData(result);
  }, [searchQuery, activeFilters, requests]);

  function convertTo24HourFormat(time12h) {
    if (!time12h) return "";
    const [time, modifier] = time12h.trim().split(" ");
    let [hours, minutes] = time.split(":");

    hours = parseInt(hours, 10);
    if (modifier === "PM" && hours !== 12) hours += 12;
    if (modifier === "AM" && hours === 12) hours = 0;

    return `${hours.toString().padStart(2, "0")}:${minutes}`;
  }

  // console.log("selected leave: ", editingRequest)

  // fetch swipe requests
  const fetchSwipeRequests = async (page = 1) => {
    setLoading(true);
    axios
      .get(
        `https://hrmspayroll.prepstripe.com/api/swipe-records?page=${page}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        },
      )
      .then((response) => {
        // // console.log("swipe request data: ", response?.data.last_page)
        setLoading(false);
        setRequests(response.data?.data);
        setPagination({
          currentPage: response.data?.current_page || 1,
          totalPages: response.data?.last_page || 1,
          totalItems: response.data?.total || 0,
          perPage: response.data?.per_page || 10,
        });
      })
      .catch((error) => {
        setLoading(false);
        console.error("Error fetching swipe requests:", error);
      });
  };

  useEffect(() => {
    if (editingRequest) {
      setFormData({
        ...editingRequest,
        in_time: convertTo24HourFormat(editingRequest?.in_time),
        out_time: convertTo24HourFormat(editingRequest?.out_time),
      });
    }
  }, [editingRequest]);

  // Fetch shift change requests from API
  useEffect(() => {
    fetchSwipeRequests();
  }, [token]);

  // Update the in_time or out_time based on in_out_mode
  useEffect(() => {
    if (formData.in_out_mode === "In") {
      setFormData((prev) => ({ ...prev, out_time: "" })); // Reset out_time
    } else {
      setFormData((prev) => ({ ...prev, in_time: "" })); // Reset in_time
    }
  }, [formData.in_out_mode]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddOrEditRequest = async (e) => {
    e.preventDefault();

    // Clear previous errors
    setFormErrors({});
    setError(null);

    // Validate description length
    if (formData.reason.length > 200) {
      setFormErrors({ reason: "Description must not exceed 200 characters" });
      return;
    }

    setIsSubmitting(true);

    if (editingRequest) {
      // Update an existing swipe record
      try {
        const response = await fetch(
          `https://hrmspayroll.prepstripe.com/api/swipe-records/${editingRequest.id}`,
          {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(formData),
          },
        );

        if (response.ok) {
          setCurrentPage(1);
          fetchSwipeRequests(1);
          setNotification({
            message: "Swipe request updated successfully",
            type: "success",
          });
          setEditingRequest(null);
          setFormErrors({});
          resetForm();
          setShowModal(false);
        } else {
          const errorData = await response.json();

          // Handle validation errors from API
          if (errorData.errors) {
            setFormErrors(errorData.errors);
          } else {
            const errorMsg =
              errorData?.error ||
              errorData?.message ||
              "Failed to update swipe request";
            setError(errorMsg);
          }
        }
      } catch (err) {
        console.error("Error updating request:", err);
        const errorMsg =
          err?.message ||
          err?.error ||
          "An unexpected error occurred while updating the swipe request.";
        setError(errorMsg);
      } finally {
        setIsSubmitting(false);
      }
    } else {
      // Add a new swipe record
      try {
        const response = await fetch(
          "https://hrmspayroll.prepstripe.com/api/swipe-records",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ ...formData, status: "Pending" }),
          },
        );

        if (response.ok) {
          setCurrentPage(1);
          fetchSwipeRequests(1);
          setNotification({
            message: "Swipe request added successfully",
            type: "success",
          });
          setEditingRequest(null);
          setFormErrors({});
          resetForm();
          setShowModal(false);
        } else {
          const errorData = await response.json();

          // Handle validation errors from API
          if (errorData.errors) {
            setFormErrors(errorData.errors);
          } else {
            const errorMsg =
              errorData?.error ||
              errorData?.message ||
              "Failed to add swipe request";
            setError(errorMsg);
          }
        }
      } catch (err) {
        console.error("Error adding request:", err);
        const errorMsg =
          err?.error || "Something went wrong. Please try again.";
        setError(errorMsg);
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleEdit = (req) => {
    setEditingRequest(req);
    setFormData(req);
    setShowModal(true);
  };

  const handleAdd = () => {
    resetForm();
    // console.log(resetForm())
    setEditingRequest(null);
    setShowModal(true);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setCurrentPage(newPage);
      fetchSwipeRequests(newPage);
    }
  };

  const renderPageNumbers = () => {
    // console.log("total pages: ", pagination.totalPages)
    const pages = [];
    for (let i = 1; i <= pagination.totalPages; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => handlePageChange(i)}
          className={`px-4 py-2 rounded-md mx-1 ${
            currentPage === i
              ? "bg-blue-600 text-white"
              : "bg-gray-200 text-gray-700 hover:bg-gray-300"
          }`}
        >
          {i}
        </button>,
      );
    }
    return pages;
  };

  const handleDelete = async (id) => {
    try {
      const response = await fetch(
        `https://hrmspayroll.prepstripe.com/api/swipe-records/${id}`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (response.ok) {
        setRequests(requests.filter((req) => req.id !== id));
        // setNotification("Swipe request deleted successfully");
        setNotification({
          message: "Swipe request deleted successfully",
          type: "success",
        });
      } else {
        // setNotification("Failed to delete swipe request");
        setNotification({
          message: "Failed to delete swipe request",
          type: "error",
        });
      }
    } catch (error) {
      console.error("Error deleting request:", error);
    }
  };

  const resetForm = () => {
    setFormData({
      swipe_type: "Swipe",
      category: "SwipeReq",
      request_date: "",
      in_out_mode: "In",
      in_time: "10:00 AM",
      out_time: "",
      reason: "",
    });
    setFormErrors({});
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    const filtering = value !== "";
    setIsFiltering(filtering);

    if (!filtering) {
      // Search cleared, restore current page's data
      handlePageChange(currentPage); // fetchResponses will be called inside
    }
  };

  // table data filteration
  const filteredRequests =
    filter === "All"
      ? requests
      : requests.filter(
          (req) => req.request.status.toLowerCase() === filter.toLowerCase(),
        );

  // Further filter by search term
  // const searchedLeaves = filteredRequests.filter((leave) =>
  //     leave.swipe_type.toLowerCase().includes(searchTerm.toLowerCase()) ||
  //     leave.in_out_mode.toLowerCase().includes(searchTerm.toLowerCase())
  // );

  const hasTempFilters = Object.values(tempFilters).some((val) => val !== "");

  return (
    <div className="px-4 py-2 bg-white dark:bg-gray-900 rounded-lg font-poppins">
      {/* notification compo */}
      {notification && (
        <Notification
          message={notification.message}
          type={notification.type}
          onClose={() => setNotification(null)}
        />
      )}

      {/* Breadcrumb */}
      <nav className="flex flex-wrap mb-4 font-poppins" aria-label="Breadcrumb">
        <ol className="inline-flex items-center space-x-1 md:space-x-2 rtl:space-x-reverse">
          <li>
            <div className="flex items-center">
              <Link
                to={getRoleBasedRouteLocal("/admin/attendance_management")}
                className="text-sm sm:text-md font-medium text-black hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-500"
              >
                Attendance Management
              </Link>
            </div>
          </li>
          <li aria-current="page">
            <div className="flex items-center">
              <svg
                className="rtl:rotate-180 w-3 h-3 text-gray-400 mx-1"
                aria-hidden="true"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 6 10"
              >
                <path
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="m1 9 4-4-4-4"
                />
              </svg>
              <span className="ms-1 text-sm sm:text-md font-medium text-gray-500 md:ms-2 dark:text-gray-400">
                Swipe Requests
              </span>
            </div>
          </li>
        </ol>
      </nav>

      <h2 className="text-lg sm:text-xl dark:text-white font-semibold mb-4">
        Swipe Requests
      </h2>

      {/* Actions */}
      <div className="flex flex-col sm:flex-row gap-2 justify-between items-center mb-4">
        {/* search field */}
        <div className="relative w-full sm:w-96">
          <input
            type="text"
            placeholder="Search..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full dark:bg-black dark:text-white dark:border-gray-700 placeholder:text-xs md:placeholder:text-sm p-2 pr-8 border border-gray-300 rounded-md"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery("")}
              className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              title="Clear search"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        {/* Add button at right end */}
        <button
          onClick={() => {
            const hasManager =
              user?.manager ||
              user?.reporting_to ||
              user?.manager_id ||
              user?.hod_department_id;
            const isAdminOrHR =
              roles?.includes("Admin") || roles?.includes("HR");

            if (!hasManager && !isAdminOrHR) {
              setNotification({
                message: "Manager is not allocated.",
                type: "error",
              });
              return;
            }
            handleAdd(true);
          }}
          className="flex items-center justify-center gap-2 font-medium bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md whitespace-nowrap"
        >
          <FaPlus /> Apply Swipe Request
        </button>
      </div>

      {/* Requests Table */}
      <div className="overflow-x-auto bg-white dark:bg-gray-900 font-poppins shadow-md rounded-lg">
        <table className="min-w-full table-auto md:table-fixed text-sm">
          <thead className="bg-gray-100 dark:bg-gray-800 dark:text-white">
            <tr>
              <th className="py-2 px-4 dark:text-white text-center font-semibold text-xs md:text-sm text-black whitespace-nowrap">
                Sr No.
              </th>
              <th className="py-2 px-4 dark:text-white text-center font-semibold text-xs md:text-sm text-black whitespace-nowrap">
                Requested Date
              </th>
              <th className="py-2 px-4 dark:text-white text-center font-semibold text-xs md:text-sm text-black whitespace-nowrap">
                In/Out Mode
              </th>
              <th className="py-2 px-4 dark:text-white text-center font-semibold text-xs md:text-sm text-black whitespace-nowrap">
                Time
              </th>
              <th className="py-2 px-4 dark:text-white text-center font-semibold text-xs md:text-sm text-black whitespace-nowrap">
                Status
              </th>
              <th className="py-2 px-4 dark:text-white text-center font-semibold text-xs md:text-sm text-black whitespace-nowrap">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr className="border-b hover:bg-gray-100 dark:hover:bg-gray-800">
                <td
                  colSpan="7"
                  className="py-2 px-4 text-xs md:text-sm whitespace-nowrap"
                >
                  <div className="animate-spin h-6 w-6 border-2 border-blue-500 border-t-transparent rounded-full mx-auto mb-2 mt-2"></div>
                </td>
              </tr>
            ) : filteredData.length > 0 ? (
              filteredData?.map((req, index) => (
                <tr
                  key={req.id}
                  className="dark:border-gray-700 dark:hover:bg-gray-950 dark:hover:text-white dark:text-white"
                >
                  <td className="py-2 px-4 dark:text-white text-center text-xs md:text-sm whitespace-nowrap font-medium">
                    {index + 1}
                  </td>
                  <td className="py-2 px-4 dark:text-white text-center text-xs md:text-sm whitespace-nowrap">
                    {req.request_date}
                  </td>
                  <td className="py-2 px-4 dark:text-white text-center text-xs md:text-sm whitespace-nowrap">
                    {req.in_out_mode}
                  </td>
                  <td className="py-2 px-4 dark:text-white text-center text-xs md:text-sm whitespace-nowrap font-medium">
                    {req.in_time || req.out_time || "N/A"}
                  </td>
                  <td className="p-2 dark:text-white text-center truncate whitespace-nowrap">
                    <StatusBadge status={req?.request?.status} />
                  </td>
                  <td className="p-2 text-center whitespace-nowrap">
                    <div className="relative inline-block">
                      <button
                        ref={openMenu === req.id ? buttonRef : null}
                        className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors focus:outline-none"
                        onClick={() =>
                          setOpenMenu(openMenu === req.id ? null : req.id)
                        }
                      >
                        <MoreVertical className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                      </button>

                      {openMenu === req.id && (
                        <DropdownMenu
                          buttonRef={buttonRef}
                          onClose={() => setOpenMenu(null)}
                          align="right"
                        >
                          <button
                            className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700"
                            onClick={() => {
                              setSelectedRequest(req);
                              setDrawerOpen(true);
                              setOpenMenu(null);
                            }}
                          >
                            View
                          </button>
                          {req.request.status === "pending" && (
                            <button
                              className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700"
                              onClick={() => {
                                handleEdit(req);
                                setOpenMenu(null);
                              }}
                            >
                              Edit
                            </button>
                          )}
                          <DeleteButton
                            style={
                              "block w-full text-left px-4 py-2 text-sm text-red-500 hover:bg-gray-100 dark:hover:bg-gray-700"
                            }
                            onDelete={() => {
                              handleDelete(req.id);
                              setOpenMenu(null);
                            }}
                          />
                        </DropdownMenu>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan={7}
                  className="text-center dark:text-white text-gray-500 p-2"
                >
                  No record found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {!isFiltering && filteredData?.length > 0 && (
        <ReactPaginate
          breakLabel="..."
          nextLabel="Next >"
          onPageChange={(event) => handlePageChange(event.selected + 1)}
          forcePage={currentPage - 1}
          pageRangeDisplayed={5}
          disabledClassName="opacity-50 cursor-not-allowed"
          disabledLinkClassName="pointer-events-none"
          pageCount={pagination.totalPages}
          previousLabel="< Prev"
          previousClassName="dark:text-white border border-gray-300 rounded-md dark:border-gray-600"
          previousLinkClassName="block w-full h-full px-3 py-1 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
          nextClassName="dark:text-white border border-gray-300 rounded-md dark:border-gray-600"
          nextLinkClassName="block w-full h-full px-3 py-1 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
          renderOnZeroPageCount={null}
          containerClassName="flex items-center justify-center mt-4 font-poppins"
          pageClassName="mx-1 border rounded dark:text-white font-poppins"
          pageLinkClassName="block w-full h-full px-3 py-1 cursor-pointer"
          activeClassName="bg-blue-500 text-white dark:bg-black dark:text-white dark:border-gray-700 font-poppins"
          activeLinkClassName="bg-blue-500 text-white dark:bg-black dark:text-white hover:bg-blue-600 dark:hover:bg-gray-900"
        />
      )}

      {/* Swipe request form (Modal) */}
      {showModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-end md:items-center z-[1001] backdrop-blur-sm"
          onClick={() => {
            setShowModal(false);
            setEditingRequest(null);
          }}
        >
          {/* Modal Container */}
          <div
            className="bg-white font-poppins dark:bg-gray-900 rounded-md shadow-xl w-full max-w-md mx-auto relative transition-transform duration-300 transform md:animate-none animate-slideUp"
            onClick={(e) => e.stopPropagation()}
          >
            <form
              onSubmit={handleAddOrEditRequest}
              className=" bg-white dark:bg-gray-900 sm:rounded-lg rounded-t-2xl"
            >
              {/* modal title */}

              <div className="relative">
                <div className="absolute inset-0  dark:from-gray-800 dark:to-gray-850"></div>
                <div className="relative flex items-center justify-between p-3 ">
                  <div className="flex items-center space-x-3">
                    {/* <div className="w-1 h-6 bg-gradient-to-b from-blue-500 to-purple-600 rounded-full"></div> */}
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-white tracking-tight">
                      {editingRequest ? "Edit Swipe Request" : "Swipe Request"}
                    </h2>
                  </div>

                  {/* Enhanced close button */}
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      setEditingRequest(null);
                    }}
                    className="group relative p-2 rounded-xl transition-all duration-200 hover:scale-105 active:scale-95"
                    aria-label="Close modal"
                  >
                    <X
                      size={20}
                      className="text-gray-600 transition-colors duration-200"
                    />
                    <div className="absolute inset-0 rounded-xl transition-all duration-200"></div>
                  </button>
                </div>
              </div>

              {/* modal body */}
              <div className="max-h-[80vh] overflow-y-auto flex flex-col gap-y-2 rounded-lg bg-white dark:bg-gray-900 p-4">
                {error && (
                  <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 px-4 py-3 rounded-md mb-4 flex items-start gap-2">
                    <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                    <span className="text-sm font-medium">{error}</span>
                  </div>
                )}
                <label className="block md:text-md text-sm dark:text-white">
                  Type: <span className="text-red-500 ml-1">*</span>
                  <select
                    required
                    value={formData.swipe_type}
                    onChange={(e) => {
                      setFormData({ ...formData, swipe_type: e.target.value });
                      if (formErrors.swipe_type)
                        setFormErrors({ ...formErrors, swipe_type: "" });
                    }}
                    className={`w-full dark:bg-black dark:text-white dark:focus:outline-none dark:border-gray-700 border p-2 rounded-md mt-1 ${
                      formErrors.swipe_type
                        ? "border-red-500 focus:border-red-500"
                        : ""
                    }`}
                  >
                    <option value="Swipe">Swipe</option>
                    {/* <option value="OD">OD</option> */}
                  </select>
                  {formErrors.swipe_type && (
                    <p className="text-sm text-red-500 mt-1 flex items-center gap-1">
                      <AlertTriangle className="w-4 h-4" />
                      {formErrors.swipe_type}
                    </p>
                  )}
                </label>

                {/* category field */}
                {/* <label className="block md:text-md text-sm dark:text-white">
                                    Category: <span className="text-red-500 ml-1">*</span>
                                    <select value={formData.category} onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                                        className="w-full dark:bg-black dark:text-white dark:focus:outline-none dark:border-gray-700 border p-2 rounded-md mt-1">
                                        <option value="SwipeReq">SwipeReq</option>
                                    </select>
                                </label> */}

                {/* requested date and in/out mode */}
                <div className="grid grid-cols-2 gap-4 md:text-md text-sm ">
                  <div className="dark:text-white">
                    Request Date: <span className="text-red-500 ml-1">*</span>
                    <input
                      type="date"
                      value={formData.request_date}
                      onChange={(e) => {
                        setFormData({
                          ...formData,
                          request_date: e.target.value,
                        });
                        if (formErrors.request_date)
                          setFormErrors({ ...formErrors, request_date: "" });
                      }}
                      min={
                        new Date(
                          new Date().getFullYear(),
                          new Date().getMonth(),
                          1,
                        )
                          .toISOString()
                          .split("T")[0]
                      }
                      max={new Date().toISOString().split("T")[0]} // Today's date
                      className={`w-full dark:bg-black dark:text-white dark:focus:outline-none dark:border-gray-700 border p-2 rounded-md mt-1 ${
                        formErrors.request_date
                          ? "border-red-500 focus:border-red-500"
                          : ""
                      }`}
                      required
                    />
                    {formErrors.request_date && (
                      <p className="text-sm text-red-500 mt-1 flex items-center gap-1">
                        <AlertTriangle className="w-4 h-4" />
                        {formErrors.request_date}
                      </p>
                    )}
                  </div>

                  <div className="dark:text-white">
                    In/Out Mode: <span className="text-red-500 ml-1">*</span>
                    <select
                      value={formData.in_out_mode}
                      onChange={(e) => {
                        setFormData({
                          ...formData,
                          in_out_mode: e.target.value,
                        });
                        if (formErrors.in_out_mode)
                          setFormErrors({ ...formErrors, in_out_mode: "" });
                      }}
                      required
                      className={`w-full dark:bg-black dark:text-white dark:focus:outline-none dark:border-gray-700 border p-2 rounded-md mt-1 ${
                        formErrors.in_out_mode
                          ? "border-red-500 focus:border-red-500"
                          : ""
                      }`}
                    >
                      <option value="In">In</option>
                      <option value="Out">Out</option>
                    </select>
                    {formErrors.in_out_mode && (
                      <p className="text-sm text-red-500 mt-1 flex items-center gap-1">
                        <AlertTriangle className="w-4 h-4" />
                        {formErrors.in_out_mode}
                      </p>
                    )}
                  </div>
                </div>

                {/* In Time/Out Time */}
                <div className="grid grid-cols-2 gap-4 md:text-md text-sm">
                  {/* Conditionally Render In Time or Out Time */}
                  {formData.in_out_mode === "In" && (
                    <div className="mb-4">
                      <label className="block dark:text-white text-gray-700 font-medium mb-1">
                        In Time: <span className="text-red-500 ml-1">*</span>
                      </label>
                      <input
                        type="time"
                        name="in_time"
                        value={formData.in_time}
                        onChange={(e) => {
                          handleChange(e);
                          if (formErrors.in_time)
                            setFormErrors({ ...formErrors, in_time: "" });
                        }}
                        required
                        className={`border dark:bg-black dark:text-white dark:focus:outline-none dark:border-gray-700 p-2 rounded-md w-full ${
                          formErrors.in_time
                            ? "border-red-500 focus:border-red-500"
                            : ""
                        }`}
                      />
                      {formErrors.in_time && (
                        <p className="text-sm text-red-500 mt-1 flex items-center gap-1">
                          <AlertTriangle className="w-4 h-4" />
                          {formErrors.in_time}
                        </p>
                      )}
                    </div>
                  )}
                  {formData.in_out_mode === "Out" && (
                    <div className="mb-4">
                      <label className="block dark:text-white text-gray-700 font-medium mb-1">
                        Out Time: <span className="text-red-500 ml-1">*</span>
                      </label>
                      <input
                        type="time"
                        name="out_time"
                        value={formData.out_time}
                        onChange={(e) => {
                          handleChange(e);
                          if (formErrors.out_time)
                            setFormErrors({ ...formErrors, out_time: "" });
                        }}
                        required
                        className={`border dark:bg-black dark:text-white dark:focus:outline-none dark:border-gray-700 p-2 rounded-md w-full ${
                          formErrors.out_time
                            ? "border-red-500 focus:border-red-500"
                            : ""
                        }`}
                      />
                      {formErrors.out_time && (
                        <p className="text-sm text-red-500 mt-1 flex items-center gap-1">
                          <AlertTriangle className="w-4 h-4" />
                          {formErrors.out_time}
                        </p>
                      )}
                    </div>
                  )}
                </div>

                {/* reason field */}
                <label className="block md:text-md text-sm dark:text-white">
                  Reason: <span className="text-red-500 ml-1">*</span>
                  <textarea
                    value={formData.reason}
                    onChange={(e) => {
                      const input = e.target.value;
                      const filteredInput = input.replace(
                        /[^a-zA-Z\s.,!?']/g,
                        "",
                      );
                      setFormData({ ...formData, reason: filteredInput });
                      // Clear error when user starts typing
                      if (formErrors.reason) {
                        setFormErrors({ ...formErrors, reason: "" });
                      }
                    }}
                    required
                    className={`w-full dark:bg-black dark:text-white dark:focus:outline-none dark:border-gray-700 border p-2 rounded-md ${
                      formErrors.reason
                        ? "border-red-500 focus:border-red-500"
                        : ""
                    }`}
                  ></textarea>
                  <div className="flex items-center justify-between mt-1">
                    <p
                      className={`text-sm ${
                        formData.reason.length > 200
                          ? "text-red-500 font-semibold"
                          : "text-gray-500"
                      }`}
                    >
                      {formData.reason.length}/200 characters
                    </p>
                  </div>
                  {formErrors.reason && (
                    <p className="text-sm text-red-500 mt-1 flex items-center gap-1">
                      <AlertTriangle className="w-4 h-4" />
                      {formErrors.reason}
                    </p>
                  )}
                </label>
              </div>

              {/* modal footer */}
              <div className="px-4 py-5 flex justify-end gap-2 items-center">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setEditingRequest(null);
                    setError(null);
                  }}
                  className="px-7 py-2 text-black hover:bg-gray-200 font-medium dark:bg-black dark:hover:bg-gray-900 dark:text-white dark:focus:outline-none border border-gray-300 rounded-md"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="bg-blue-600 hover:bg-blue-700 font-medium text-white px-10 py-2 rounded-md flex items-center gap-2 justify-center min-w-[120px]"
                >
                  {isSubmitting && (
                    <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                  )}
                  {isSubmitting
                    ? "Submitting..."
                    : editingRequest
                      ? "Update"
                      : "Save"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal to view details */}
      {drawerOpen && selectedRequest && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-[1001] backdrop-blur-sm p-4"
          onClick={() => setDrawerOpen(false)}
        >
          <div
            className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header with Gradient */}
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 px-6 py-5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2.5 bg-white/20 rounded-xl backdrop-blur-sm">
                    <svg
                      className="w-6 h-6 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                      />
                    </svg>
                  </div>
                  <h3 className="text-2xl font-bold text-white">
                    Swipe Request Details
                  </h3>
                </div>
                <button
                  onClick={() => setDrawerOpen(false)}
                  className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                >
                  <X className="w-6 h-6 text-white" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
              {/* Status Badge - Centered */}
              <div className="mb-6 flex justify-center">
                <StatusBadge status={selectedRequest.request.status} />
              </div>

              {/* Information Cards Grid - 2x2 */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                {/* Employee Name */}
                <div className="bg-gray-50 dark:bg-gray-800/50 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                      <svg
                        className="w-5 h-5 text-blue-600 dark:text-blue-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                        />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
                        Employee Name
                      </p>
                      <p className="text-base font-semibold text-gray-900 dark:text-white">
                        {user?.name || "HOD User"}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Request Date */}
                <div className="bg-gray-50 dark:bg-gray-800/50 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                      <svg
                        className="w-5 h-5 text-green-600 dark:text-green-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                        />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
                        Request Date
                      </p>
                      <p className="text-base font-semibold text-gray-900 dark:text-white">
                        {selectedRequest.request_date}
                      </p>
                    </div>
                  </div>
                </div>

                {/* In/Out Mode */}
                <div className="bg-gray-50 dark:bg-gray-800/50 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-orange-100 dark:bg-orange-900/30 rounded-lg">
                      <svg
                        className="w-5 h-5 text-orange-600 dark:text-orange-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1"
                        />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
                        In/Out Mode
                      </p>
                      <p className="text-base font-semibold text-gray-900 dark:text-white">
                        {selectedRequest.in_out_mode}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Time */}
                <div className="bg-gray-50 dark:bg-gray-800/50 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                      <svg
                        className="w-5 h-5 text-purple-600 dark:text-purple-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
                        Time
                      </p>
                      <p className="text-base font-semibold text-gray-900 dark:text-white">
                        {selectedRequest.in_out_mode === "In"
                          ? selectedRequest.in_time
                          : selectedRequest.out_time}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Reason Section */}
              <div className="bg-gray-50 dark:bg-gray-800/50 rounded-xl p-5 border border-gray-200 dark:border-gray-700">
                <h4 className="text-base font-bold text-gray-800 dark:text-white mb-3">
                  Reason
                </h4>
                <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
                  {selectedRequest.reason || "No reason provided"}
                </p>
              </div>

              {/* Approver Comments Section */}
              {selectedRequest.request.status !== "pending" && (
                <div className="mt-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl p-5 border border-blue-200 dark:border-blue-800">
                  <h4 className="text-base font-bold text-blue-600 dark:text-blue-400 mb-3">
                    Approver's Comment
                  </h4>
                  <p className="text-sm text-gray-800 dark:text-gray-200 mb-2 italic">
                    {selectedRequest.request.approver_comments ? (
                      `"${selectedRequest.request.approver_comments}"`
                    ) : (
                      <span className="text-gray-400">No comments provided.</span>
                    )}
                  </p>
                  {selectedRequest.request.action_date && (
                    <p className="text-xs text-gray-600 dark:text-gray-400">
                      Request {selectedRequest.request.status} on:{" "}
                      {selectedRequest.request.action_date}
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Footer with Action Buttons */}
            <div className="px-6 py-4 bg-gray-50 dark:bg-gray-900/50 border-t dark:border-gray-800 flex justify-end gap-3">
              <button
                onClick={() => setDrawerOpen(false)}
                className="px-6 py-2.5 rounded-xl border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300 font-medium hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              >
                Close
              </button>
              {/* Show Reject and Approve buttons only for approvers and if status is pending */}
              {selectedRequest.request.status === "pending" &&
                (roles?.includes("approver") || roles?.includes("admin")) && (
                  <>
                    <button
                      onClick={() => {
                        // Handle reject action
                        // You'll need to implement this function
                        console.log("Reject request:", selectedRequest.id);
                        setDrawerOpen(false);
                      }}
                      className="px-6 py-2.5 rounded-xl bg-red-600 text-white font-medium hover:bg-red-700 transition-colors flex items-center gap-2"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"
                        />
                      </svg>
                      Reject
                    </button>
                    <button
                      onClick={() => {
                        // Handle approve action
                        // You'll need to implement this function
                        console.log("Approve request:", selectedRequest.id);
                        setDrawerOpen(false);
                      }}
                      className="px-6 py-2.5 rounded-xl bg-blue-600 text-white font-medium hover:bg-blue-700 transition-colors flex items-center gap-2"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                      Approve
                    </button>
                  </>
                )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SwipeRequest;
